import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Building2, Users2, Clock, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS = {
  new:       { label: "New",       color: "#06B6D4" },
  contacted: { label: "Contacted", color: "#3B82F6" },
  won:       { label: "Won",       color: "#10B981" },
  lost:      { label: "Lost",      color: "#EF4444" },
  archived:  { label: "Archived",  color: "#6B7280" },
};

const PLAN_META = {
  starter:    { label: "Starter",    color: "#06B6D4" },
  growth:     { label: "Growth",     color: "#3B82F6" },
  enterprise: { label: "Enterprise", color: "#10B981" },
  custom:     { label: "Custom",     color: "#A78BFA" },
  contact:    { label: "Enquiry",    color: "#F59E0B" },
};

const PLAN_ORDER = ["starter", "growth", "enterprise", "custom", "contact"];

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error("Could not load quotes");
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (status, extraNote) => {
    if (!selected) return;
    try {
      const body = { status };
      if (extraNote !== undefined) body.internal_note = extraNote;
      const { data } = await api.patch(`/admin/quotes/${selected.request_id}`, body);
      setQuotes((prev) => prev.map((q) => q.request_id === data.request_id ? data : q));
      setSelected(data);
      toast.success(`Marked ${STATUS[status].label.toLowerCase()}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const filtered = quotes.filter((q) =>
    (filter === "all" || q.status === filter) &&
    (planFilter === "all" || q.plan === planFilter)
  );
  const counts = Object.fromEntries(Object.keys(STATUS).map((k) => [k, quotes.filter((q) => q.status === k).length]));
  const planCounts = Object.fromEntries(PLAN_ORDER.map((k) => [k, quotes.filter((q) => q.plan === k).length]));

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Link to="/recruiter" className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="mono-label">admin</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">Quote requests</h1>
          <p className="text-slate-400 mt-1">Inbound leads from the pricing page.</p>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(STATUS).map(([k, meta]) => (
            <button
              key={k}
              onClick={() => setFilter(filter === k ? "all" : k)}
              data-testid={`quotes-filter-${k}`}
              className={`text-left px-3 py-2 rounded-lg border transition-colors ${filter === k ? "border-white/25 bg-white/5" : "border-white/10 hover:border-white/20"}`}
            >
              <div className="mono-label" style={{ color: meta.color }}>{meta.label}</div>
              <div className="font-display text-xl mt-1">{counts[k] || 0}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <div className="mono-label text-slate-500">filter by plan</div>
        <div className="flex flex-wrap gap-2" data-testid="quotes-plan-filter-row">
          <button
            onClick={() => setPlanFilter("all")}
            data-testid="quotes-plan-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${planFilter === "all" ? "border-white/30 bg-white/5 text-white" : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"}`}
          >
            All plans <span className="text-slate-500 ml-1">{quotes.length}</span>
          </button>
          {PLAN_ORDER.map((k) => {
            const meta = PLAN_META[k];
            const active = planFilter === k;
            return (
              <button
                key={k}
                onClick={() => setPlanFilter(active ? "all" : k)}
                data-testid={`quotes-plan-filter-${k}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors border inline-flex items-center gap-2"
                style={{
                  color: active ? meta.color : "#94A3B8",
                  backgroundColor: active ? `${meta.color}22` : "transparent",
                  borderColor: active ? `${meta.color}66` : "#ffffff1a",
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                {meta.label}
                <span className="opacity-60">{planCounts[k] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 mono-label text-slate-500">
          <div className="col-span-3">Requester</div>
          <div className="col-span-3">Company / Team</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {filtered.map((q, idx) => {
          const meta = STATUS[q.status] || STATUS.new;
          const d = new Date(q.created_at);
          return (
            <button
              key={q.request_id}
              onClick={() => { setSelected(q); setNote(q.internal_note || ""); }}
              data-testid={`quote-row-${q.request_id}`}
              className={`w-full grid grid-cols-12 items-center px-5 py-4 text-left hover:bg-white/5 transition-colors ${idx !== filtered.length - 1 ? "border-b border-white/5" : ""}`}
            >
              <div className="col-span-3">
                <div className="text-sm font-medium text-slate-100">{q.name}</div>
                <div className="text-xs text-slate-500">{q.email}</div>
              </div>
              <div className="col-span-3">
                <div className="text-sm text-slate-200">{q.company || "—"}</div>
                <div className="text-xs text-slate-500">{q.team_size || "—"}</div>
              </div>
              <div className="col-span-2">
                {(() => {
                  const pm = PLAN_META[q.plan] || { label: q.plan, color: "#94A3B8" };
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ color: pm.color, background: `${pm.color}1A`, border: `1px solid ${pm.color}33` }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: pm.color }} />
                      {pm.label}
                    </span>
                  );
                })()}
              </div>
              <div className="col-span-2 text-sm text-slate-400">{d.toLocaleDateString()} · {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="col-span-2 flex justify-end">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: meta.color, background: `${meta.color}22`, border: `1px solid ${meta.color}33` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                  {meta.label}
                </span>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            {quotes.length === 0 ? "No quote requests yet." : "No quotes match this filter."}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <Row icon={Mail} label="Email">
                  <a href={`mailto:${selected.email}`} className="text-[#06B6D4] hover:underline">{selected.email}</a>
                </Row>
                <Row icon={Building2} label="Company">{selected.company || "—"}</Row>
                <Row icon={Users2} label="Team size">{selected.team_size || "—"}</Row>
                <Row icon={Clock} label="Plan"><span className="capitalize">{selected.plan}</span></Row>
              </div>
              {selected.message && (
                <div className="mt-4 p-4 bg-[#0B0F19] rounded-lg text-sm text-slate-300 leading-relaxed border border-white/5">
                  {selected.message}
                </div>
              )}

              <div className="mt-4 space-y-2">
                <div className="mono-label">internal note</div>
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => note !== (selected.internal_note || "") && updateStatus(selected.status, note)}
                  data-testid="quote-note-input"
                  placeholder="Notes visible to recruiters only"
                  className="bg-[#0B0F19] border-white/10 text-sm"
                />
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-white/10 pt-4">
                <span className="mono-label mr-2">set status</span>
                {Object.entries(STATUS).filter(([k]) => k !== selected.status).map(([k, meta]) => (
                  <Button
                    key={k}
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(k)}
                    data-testid={`quote-set-${k}`}
                    className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white h-7 rounded-full text-xs"
                  >
                    <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: meta.color }} />
                    {meta.label}
                  </Button>
                ))}
              </div>

              <Button
                onClick={() => {
                  const subj = `Re: your Axiony quote request`;
                  window.location.href = `mailto:${selected.email}?subject=${encodeURIComponent(subj)}`;
                }}
                data-testid="quote-reply-btn"
                className="w-full mt-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full"
              >
                <Mail className="w-4 h-4 mr-2" /> Reply by email
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-3 text-sm">
    <Icon className="w-4 h-4 text-slate-500" />
    <span className="text-slate-400 w-24">{label}</span>
    <span className="text-slate-200">{children}</span>
  </div>
);
